<template>
	<div
		class="
			c-book-mayor-card-form
			rounded-lg
			p-12
			mb-sm
			flex flex-col
			transform
			duration-300
			ease-smooth-out
		"
		:class="open ? 'bg-primary-medium' : 'bg-primary-light'"
	>
		<div class="flex relative cursor-pointer" @click="toggleCard">
			<div
				v-if="meeting.date"
				class="
					rounded-lg
					w-96
					px-35
					text-primary-button
					flex flex-col
					justify-center
					items-center
					transform
					duration-300
					ease-smooth-out
				"
				:class="open ? 'bg-primary' : 'bg-primary-medium'"
				v-html="formatDate(meeting.date)"
			></div>
			<div class="flex flex-col ml-40">
				<div
					v-if="meeting.location"
					class="text-black font-semibold text-h2 mb-8"
				>
					{{ meeting.location }}
				</div>
				<div v-if="meeting.address" class="uppercase text-h8 mb-5 flex">
					<SvgLocation class="w-16 mr-12" />{{ meeting.address }}
				</div>
				<div
					v-if="meeting.meetingResponsible"
					class="uppercase text-h8 mb-5 flex"
				>
					<SvgPerson class="w-16 mr-12" />{{
						meeting.meetingResponsible
					}}
				</div>
				<div v-if="meeting.timeInterval" class="uppercase text-h8 flex">
					<SvgClock class="w-16 mr-12" />{{ meeting.timeInterval }}
				</div>
			</div>
			<div class="flex items-center absolute right-24 h-full">
				<div
					v-if="meeting.timeslotOverview"
					class="
						py-8
						px-14
						rounded-full
						text-primary-button
						<1024:hidden
						transform
						duration-300
						ease-smooth-out
					"
					:class="open ? 'bg-primary' : 'bg-primary-medium'"
				>
					{{ availableTimeslots(meeting.timeslotOverview).length }}
				</div>
				<span
					class="
						uppercase
						text-primary-button
						font-semibold
						text-h8
						ml-8
						<1024:hidden
					"
					>Ledige tider</span
				>
				<SvgCaret
					:class="[
						'transform w-12 ml-24',
						'duration-300 ease-smooth-out',
						{ 'rotate-90': !open },
						{ '-rotate-90': open },
					]"
				/>
			</div>
		</div>
		<form
			:class="
				open
					? 'c-book-mayor-card-form-panel--open'
					: 'c-book-mayor-card-form-panel'
			"
			@submit.prevent="onSubmit"
		>
			<div
				v-if="meeting.practicalInformation"
				v-html="meeting.practicalInformation"
			></div>
			<div
				v-if="computedFormValues['Vælg tidspunkt']"
				class="text-h4 font-semibold mt-md"
				v-html="computedFormValues['Vælg tidspunkt'].name"
			></div>
			<BaseInputDropdown
				v-if="alteredTimeslots"
				class="w-full flex-grow mt-sm"
				:config="alteredTimeslots"
				:aria-label="
					alteredTimeslots.placeholder || 'Vælg et tidspunkt'
				"
				book-mayor
				@input="(event) => updateTimeslot(event)"
			/>
			<span class="text-h7 text-text">Påkrævet</span>
			<div v-if="requiredError" class="text-h7" style="color: red">
				{{ requiredError }}
			</div>
			<div
				v-if="computedFormValues['Kontaktoplysninger']"
				class="text-h4 font-semibold mt-md"
				v-html="computedFormValues['Kontaktoplysninger'].name"
			></div>
			<div
				v-if="computedFormValues['Dit navn']"
				class="font-semibold mt-sm"
				v-html="computedFormValues['Dit navn'].name"
			></div>
			<input
				v-model="name"
				type="text"
				:name="computedFormValues['Dit navn'].placeholder"
				:placeholder="computedFormValues['Dit navn'].placeholder"
				pattern="[A-Za-zÆØÅæøå]{2,}.*"
				:class="[
					'text-h5 h-56 py-xs px-sm text-text flex-grow mt-xs',
					'w-full bg-white rounded-md border-2 border-primary-button',
				]"
				required
			/>
			<span class="text-h7 text-text">Påkrævet</span>
			<div class=">=768:flex >=768:justify-between">
				<div class=">=768:w-2col">
					<div
						v-if="computedFormValues['Postnummer']"
						class="font-semibold mt-sm"
						v-html="computedFormValues['Postnummer'].name"
					></div>
					<input
						v-model="postcode"
						type="text"
						pattern="[0-9]{4,}"
						:name="computedFormValues['Postnummer'].placeholder"
						:placeholder="
							computedFormValues['Postnummer'].placeholder
						"
						:class="[
							'text-h5 h-56 py-xs px-sm text-text flex-grow mt-xs',
							'w-full bg-white rounded-md border-2 border-primary-button',
						]"
						required
					/>
					<span class="text-h7 text-text">Påkrævet</span>
				</div>
				<div class=">=768:w-3col">
					<div
						v-if="computedFormValues['Adresse']"
						class="font-semibold mt-sm"
						v-html="computedFormValues['Adresse'].name"
					></div>
					<input
						v-model="address"
						type="text"
						pattern="[A-Za-zÆØÅæøå]{3,}.*"
						:name="computedFormValues['Adresse'].placeholder"
						:placeholder="computedFormValues['Adresse'].placeholder"
						:class="[
							'text-h5 h-56 py-xs px-sm text-text flex-grow mt-xs',
							'w-full bg-white rounded-md border-2 border-primary-button',
						]"
						required
					/>
					<span class="text-h7 text-text">Påkrævet</span>
				</div>
			</div>
			<div class=">=768:flex >=768:justify-between">
				<div class=">=768:w-2col">
					<div
						v-if="computedFormValues['Telefon']"
						class="font-semibold mt-sm"
						v-html="computedFormValues['Telefon'].name"
					></div>
					<input
						v-model="telephone"
						type="text"
						pattern="[0-9\s]{1,}|\++[0-9\s]{1,}"
						:name="computedFormValues['Telefon'].placeholder"
						:placeholder="computedFormValues['Telefon'].placeholder"
						:class="[
							'text-h5 h-56 py-xs px-sm text-text flex-grow mt-xs',
							'w-full bg-white rounded-md border-2 border-primary-button',
						]"
						required
					/>
					<span class="text-h7 text-text">Påkrævet</span>
				</div>
				<div class=">=768:w-3col">
					<div
						v-if="computedFormValues['E-mail']"
						class="font-semibold mt-sm"
						v-html="computedFormValues['E-mail'].name"
					></div>
					<input
						v-model="email"
						type="email"
						:name="computedFormValues['E-mail'].placeholder"
						:placeholder="computedFormValues['E-mail'].placeholder"
						:class="[
							'text-h5 h-56 py-xs px-sm text-text flex-grow mt-xs',
							'w-full bg-white rounded-md border-2 border-primary-button',
						]"
						required
					/>
					<span class="text-h7 text-text">Påkrævet</span>
				</div>
			</div>
			<div
				v-if="computedFormValues['Emne']"
				class="font-semibold mt-sm"
				v-html="computedFormValues['Emne'].name"
			></div>
			<input
				v-model="subject"
				type="text"
				pattern="\S(?:\s*\S){1}.*\S"
				:name="computedFormValues['Emne'].placeholder"
				:placeholder="computedFormValues['Emne'].placeholder"
				:class="[
					'text-h5 h-70 py-xs px-sm text-text flex-grow mt-xs',
					'w-full bg-white rounded-md border-2 border-primary-button',
				]"
				required
			/>
			<span class="text-h7 text-text">Påkrævet</span>
			<div
				v-if="computedFormValues['Personfølsomme oplysninger']"
				class="font-semibold mt-md"
				v-html="computedFormValues['Personfølsomme oplysninger'].name"
			></div>
			<div
				v-if="computedFormValues['Obs']"
				class="mt-6"
				v-html="computedFormValues['Obs'].value"
			></div>
			<div
				class="
					mt-sm
					flex
					items-center
					c-book-mayor-card-form-panel-checkbox
				"
			>
				<input
					type="checkbox"
					class="c-book-mayor-card-form-panel-checkbox--checkmark"
					aria-labelledby="c-book-mayor-card__checkbox-label"
					required
					@input="(event) => updateValue('checked', event)"
				/>
				<div id="c-book-mayor-card__checkbox-label" class="ml-8">
					Jeg accepterer disse vilkår
				</div>
			</div>
			<BaseButton
				:disabled="loading"
				:class="['my-sm ml-auto', { 'opacity-50': loading }]"
				v-text="'Book møde'"
			/>
		</form>
	</div>
</template>
<script>
import SvgCaret from '~/assets/svgs/caret-icon.svg?inline';
import SvgLocation from '~/assets/svgs/location-icon.svg?inline';
import SvgPerson from '~/assets/svgs/person-icon.svg?inline';
import SvgClock from '~/assets/svgs/clock-icon.svg?inline';

export default {
	name: 'BookMayorCardForm',
	components: { SvgCaret, SvgLocation, SvgPerson, SvgClock },
	props: {
		meeting: Object,
		formItems: Array,
		redirect: Object,
	},
	data() {
		const response = {};
		this.formItems?.forEach((element) => {
			response[element.name] = element;
		});
		const arr = Object.values({
			...response,
			timeslot: { value: null, label: 'timeslot', name: 'timeslot' },
			checked: { value: null, label: 'checked', name: 'checked' },
		});
		// eslint-disable-next-line vue/no-side-effects-in-computed-properties
		const computedFormValues = {
			...response,
			timeslot: { value: null, label: 'timeslot', name: 'timeslot' },
			checked: { value: null, label: 'checked', name: 'checked' },
		};
		return {
			open: false,
			alteredTimeslots: {},
			formDataValues: arr,
			name: '',
			postcode: '',
			address: '',
			telephone: '',
			email: '',
			subject: '',
			timeslot: '',

			error: null,
			loading: false,
			success: false,
			requiredError: '',
			computedFormValues,
		};
	},
	mounted() {
		const availableSlots = this.meeting.timeslotOverview.filter(
			({ available }) => available === true
		);
		const arr = availableSlots.map((x) => x.timeslot);
		const obj = arr.map((value) => ({
			checked: false,
			label: value,
			value: value,
		}));
		this.alteredTimeslots = {
			endpointUrl: 'null',
			label: 'Tidsrum',
			name: 'timeslots',
			placeholder: 'Tidsrum',
			type: 'Dropdown',
			value: obj,
		};
	},
	methods: {
		toggleCard() {
			this.open = !this.open;
		},
		updateValue(name, event) {
			const field = this.formDataValues.find((e) => e.name === name);

			if (typeof event === 'string' && Array.isArray(field.value)) {
				field.value.forEach((element) => {
					element.checked = element.value === event;
				});
			} else if (typeof event === 'string') {
				field.value = event;
			} else {
				field.value = event?.target?.value ?? '';
			}
		},
		updateTimeslot(event) {
			this.timeslot = event;
		},
		verifyTimeslot() {
			if (!this.timeslot) {
				this.requiredError = 'Valg af tidspunkt er påkrævet';
			}
		},
		formatDate(dateString) {
			const date = new Date(dateString);

			const day = `0${date.getDate()}`.slice(-2);
			const month = new Date(dateString).toLocaleString('en-us', {
				month: 'short',
			});

			return `<div class="text-h2 font-semibold">${day}</div> <div class="uppercase">${month}</div>`;
		},
		availableTimeslots(timeslots) {
			const availableSlots = timeslots.filter(
				({ available }) => available === true
			);
			return availableSlots;
		},
		onSubmit() {
			this.verifyTimeslot();
			if (this.timeslot) {
				const self = this;
				const redirectUrl = this.redirect.url;
				this.loading = true;
				this.$axios
					.post('api/bookthemayor/savebooking', {
						parent: this.meeting.id,
						timeslot: this.timeslot,
						citizenName: this.name,
						address: this.address,
						postalCode: this.postcode,
						phone: this.telephone,
						email: this.email,
						subjects: this.subject,
					})
					.then((response) => {
						this.loading = false;
						this.success = true;
						console.log(response);
						if (response.status === 200) {
							self.$router.push(redirectUrl);
						}
					})
					.catch((error) => {
						this.error = error;
						console.error(error);
					});
			}
		},
	},
};
</script>

<style>
.c-book-mayor-card-form-panel-checkbox input[type='checkbox'] {
	-webkit-appearance: none;
	appearance: none;
	background-color: none;
	margin: 0;
	font: inherit;
	color: currentColor;
	width: 16px;
	height: 16px;
	@apply border-2 border-primary-button;
	border-radius: 0.15em;
	transform: translateY(-0.075em);
	display: grid;
	place-content: center;
}
.c-book-mayor-card-form-panel-checkbox input[type='checkbox']:checked {
	@apply bg-primary-button;
}
.c-book-mayor-card-form-panel-checkbox input[type='checkbox']::before {
	content: '';
	width: 0.65em;
	height: 0.65em;
	transform: scale(0);
	transition: 120ms transform ease-in-out;
	transform-origin: bottom left;
	clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
	@apply bg-white;
}
.c-book-mayor-card-form-panel-checkbox input[type='checkbox']:checked::before {
	transform: scale(1);
}
.c-book-mayor-card-form-panel {
	@apply px-sm;
	max-height: 0;
	overflow: hidden;
	transition: max-height 300ms cubic-bezier(0, 1, 0, 1);
}
.c-book-mayor-card-form-panel--open {
	@apply mt-sm;
	@apply px-sm;
	max-height: 2000px;
	overflow: hidden;
	transition: max-height 300ms ease-in-out;
}
</style>
